import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ColumnStickyAddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - Column sticky button
 * @property {HTMLElement} [label] - Button label
 */

/**
 * Desktop column sticky Add to Cart shown while the static ATC is below the fold.
 *
 * @extends {Component<ColumnStickyAddToCartRefs>}
 */
class ColumnStickyAddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement | null} */
  #visibilityTarget = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {MediaQueryList | null} */
  #desktopQuery = null;

  connectedCallback() {
    super.connectedCallback();

    this.#desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.#desktopQuery.addEventListener('change', this.#handleDesktopChange, {
      signal: this.#abortController.signal,
    });

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('variant-picker:state-sync', this.#handleVariantPickerStateSync, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#abortController.abort();
  }

  handleClick = () => {
    if (this.dataset.needsSelection === 'true') {
      this.#scrollToVariantPicker();
      return;
    }

    this.#targetAddToCartButton = this.#resolveTargetAddToCartButton(this.#getProductForm());
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  #handleDesktopChange = () => {
    this.#setupIntersectionObserver();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    this.#syncStateFromProductForm();
    requestAnimationFrame(() => requestAnimationFrame(() => this.#setupIntersectionObserver()));
  };

  #handleVariantPickerStateSync = () => {
    this.#syncStateFromProductForm();
    requestAnimationFrame(() => requestAnimationFrame(() => this.#setupIntersectionObserver()));
  };

  /**
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #setupIntersectionObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#visibilityTarget = null;

    if (!this.#desktopQuery?.matches) {
      this.#setVisible(false);
      return;
    }

    const targetButton = this.#resolveTargetAddToCartButton(this.#getProductForm());
    if (!(targetButton instanceof HTMLElement)) {
      this.#setVisible(false);
      return;
    }

    this.#visibilityTarget = targetButton;
    this.#targetAddToCartButton = targetButton;

    const updateVisibility = () => {
      if (!this.#desktopQuery?.matches) {
        this.#setVisible(false);
        return;
      }

      this.#updateColumnGeometry();

      if (!(this.#visibilityTarget instanceof HTMLElement)) {
        this.#setVisible(false);
        return;
      }

      const rect = this.#visibilityTarget.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        this.#setVisible(false);
        return;
      }

      const headerOffset = this.#getHeaderOffset();
      const isAboveHeader = rect.bottom <= headerOffset;
      const visibleRatio = this.#getStaticVisibleRatio(rect);
      const threshold = this.#getVisibilityThreshold();

      // Show column CTA until at least `threshold` of the static button is on screen.
      // When the static button has scrolled above the header, hide (footer bar zone).
      const showColumn = !isAboveHeader && visibleRatio < threshold;
      this.#setVisible(showColumn);
    };

    this.#observer = new IntersectionObserver(
      () => {
        updateVisibility();
      },
      { threshold: 0 }
    );

    this.#observer.observe(targetButton);

    const { signal } = this.#abortController;
    window.addEventListener('scroll', updateVisibility, { passive: true, signal });
    window.addEventListener('resize', updateVisibility, { passive: true, signal });

    requestAnimationFrame(() => requestAnimationFrame(updateVisibility));
  }

  /**
   * Fraction of the static ATC height currently visible in the viewport (0–1).
   * @param {DOMRect} rect
   * @returns {number}
   */
  #getStaticVisibleRatio(rect) {
    const visibleTop = Math.max(rect.top, 0);
    const visibleBottom = Math.min(rect.bottom, window.innerHeight);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    return rect.height > 0 ? visibleHeight / rect.height : 0;
  }

  /**
   * Hide column sticky when this much of the static button is visible (0–1). Default 10%.
   * @returns {number}
   */
  #getVisibilityThreshold() {
    const parsed = parseFloat(this.dataset.staticVisibleThreshold ?? '');
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 1) return parsed;
    return 0.1;
  }

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    this.dataset.visible = visible ? 'true' : 'false';
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  #updateColumnGeometry() {
    const sectionElement = this.closest('.shopify-section');
    const detailsColumn = sectionElement?.querySelector('.product-information__details-desktop');
    if (!(detailsColumn instanceof HTMLElement)) return;

    const rect = detailsColumn.getBoundingClientRect();
    if (rect.width <= 0) return;

    this.style.setProperty('--col-left', `${rect.left}px`);
    this.style.setProperty('--col-width', `${rect.width}px`);
  }

  #syncStateFromProductForm() {
    const productForm = this.#getProductForm();
    if (!(productForm instanceof HTMLElement)) return;

    const variantIdInput = productForm.querySelector('[ref="variantId"]');
    const targetButton = this.#resolveTargetAddToCartButton(productForm);
    const variantId =
      variantIdInput instanceof HTMLInputElement && variantIdInput.value ? String(variantIdInput.value) : '';
    const hasResolvedVariant = Boolean(variantId);
    const isButtonDisabled = targetButton instanceof HTMLButtonElement ? targetButton.disabled : true;
    const isUnavailable =
      targetButton instanceof HTMLButtonElement &&
      targetButton.dataset.unavailable === 'true' &&
      hasResolvedVariant;

    this.#targetAddToCartButton = targetButton;
    this.dataset.needsSelection = hasResolvedVariant ? 'false' : 'true';
    this.dataset.variantAvailable = hasResolvedVariant && !isButtonDisabled && !isUnavailable ? 'true' : 'false';

    if (this.refs.addToCartButton instanceof HTMLButtonElement) {
      this.refs.addToCartButton.disabled = isUnavailable;
      if (isUnavailable) {
        this.refs.addToCartButton.dataset.unavailable = 'true';
      } else {
        delete this.refs.addToCartButton.dataset.unavailable;
      }
    }

    const label = this.refs.label;
    if (label instanceof HTMLElement) {
      if (!hasResolvedVariant) {
        label.textContent = 'Select options';
      } else if (isUnavailable) {
        const canonicalLabel = this.#readTargetButtonLabel(targetButton);
        label.textContent = canonicalLabel || label.textContent;
      } else {
        label.textContent = 'Add to bag';
      }
    }
  }

  /**
   * Reads the canonical visible label from a target add-to-cart button,
   * ignoring hidden spans (edit-mode/price) that would otherwise be
   * concatenated by reading the button's full textContent.
   * @param {HTMLButtonElement | null} targetButton
   * @returns {string}
   */
  #readTargetButtonLabel(targetButton) {
    if (!(targetButton instanceof HTMLButtonElement)) return '';
    const textNode = targetButton.querySelector('.add-to-cart-text__content-text');
    if (textNode instanceof HTMLElement) return textNode.textContent?.trim() || '';
    return targetButton.textContent?.trim() || '';
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * @param {HTMLElement | null} fallbackProductForm
   * @returns {HTMLButtonElement | null}
   */
  #resolveTargetAddToCartButton(fallbackProductForm) {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const bundleButtons = /** @type {HTMLButtonElement[]} */ (
      Array.from(
        sectionElement.querySelectorAll('.buy-buttons-block[data-bundle-buy-buttons] .add-to-cart-button')
      ).filter((button) => button instanceof HTMLButtonElement)
    );

    const visibleBundleButton = bundleButtons.find((button) => this.#isVisibleElement(button));
    if (visibleBundleButton) return visibleBundleButton;

    if (bundleButtons[0]) return bundleButtons[0];

    const formButtons = /** @type {HTMLButtonElement[]} */ (
      Array.from(
        sectionElement.querySelectorAll(
          '.product-information__details-desktop product-form-component [ref="addToCartButton"]'
        )
      ).filter((button) => button instanceof HTMLButtonElement)
    );

    const visibleFormButton = formButtons.find((button) => this.#isVisibleElement(button));
    if (visibleFormButton) return visibleFormButton;

    if (formButtons[0]) return formButtons[0];

    if (!(fallbackProductForm instanceof HTMLElement)) return null;
    const defaultButton = fallbackProductForm.querySelector('[ref="addToCartButton"]');
    return defaultButton instanceof HTMLButtonElement ? defaultButton : null;
  }

  #scrollToVariantPicker() {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return;

    const variantPickers = Array.from(sectionElement.querySelectorAll('variant-picker'));
    const variantPicker =
      variantPickers.find((picker) => this.#isVisibleElement(picker)) ?? variantPickers[0];
    if (!(variantPicker instanceof HTMLElement)) return;

    const headerOffset = this.#getHeaderOffset();
    const top = window.scrollY + variantPicker.getBoundingClientRect().top - headerOffset - 12;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  #isVisibleElement(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest('[hidden], [inert]')) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  /**
   * @returns {number}
   */
  #getHeaderOffset() {
    const rootStyles = getComputedStyle(document.documentElement);
    const headerVar = parseFloat(rootStyles.getPropertyValue('--header-group-height'));
    if (!Number.isNaN(headerVar) && headerVar > 0) return headerVar;

    const stickyHeader = document.querySelector('#header-group #header-component');
    if (stickyHeader instanceof HTMLElement) {
      return stickyHeader.getBoundingClientRect().height;
    }

    return 0;
  }
}

if (!customElements.get('column-sticky-add-to-cart')) {
  customElements.define('column-sticky-add-to-cart', ColumnStickyAddToCartComponent);
}
